// File: frontend/src/components/pages/home/Hero.js

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useLanguage } from '../../../hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, Lock, Zap } from 'lucide-react';
import AuthModal from '../../auth/AuthModal';
import styles from './Hero.module.css';
import { ASSETS_BASE_URL } from '../../../config/constants';

const Hero = () => {
  const { t, parseHTML } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const heroRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        if (rect.bottom > 0) {
          setScrollY(window.scrollY);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Preload critical assets
  useEffect(() => {
    // Preload poster image
    const posterImg = new Image();
    posterImg.src = `${ASSETS_BASE_URL}/images/hero-poster-coast.png`;
    
    // Preload hero image
    const heroImg = new Image();
    heroImg.src = `${ASSETS_BASE_URL}/images/hero-family-car.png`;
    heroImg.onload = () => setIsImageLoaded(true);

    // Start video load after a brief delay to prioritize images
    const videoLoadTimer = setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.load();
      }
    }, 100);

    return () => clearTimeout(videoLoadTimer);
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard/policies');
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <section className={styles.hero} ref={heroRef}>
      {/* Background layers */}
      <div className={styles.bgLayers}>
        {/* Video background with parallax */}
        <div
          className={styles.videoBg}
          style={{ transform: `translateY(${scrollY * 0.25}px)` }}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={`${ASSETS_BASE_URL}/images/hero-poster-coast.png`}
            className={`${styles.video} ${isVideoLoaded ? styles.videoLoaded : ''}`}
            onLoadedData={() => setIsVideoLoaded(true)}
          >
            <source 
              src={`${ASSETS_BASE_URL}/videos/hero-coastal.mp4`}
              type="video/mp4" 
            />
          </video>
          {/* Optional: Show poster with blur effect while loading */}
          {!isVideoLoaded && (
            <img 
              src={`${ASSETS_BASE_URL}/images/hero-poster-coast.png`}
              className={styles.posterOverlay}
              alt=""
            />
          )}
        </div>

        {/* Gradient overlay */}
        <div className={styles.overlay} />

        {/* Subtle grain texture */}
        <div className={styles.grain} />
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.inner}>
          {/* Left: Text */}
          <div className={styles.textBlock}>
            <div className={styles.badge}>
              <Shield size={14} strokeWidth={2.5} />
              <span>{t('hero.trust_badge', 'Trusted by 10,000+ Policyholders')}</span>
            </div>

            <h1 className={styles.headline}>
              {parseHTML(
                t('hero.headline_3', 'Your Personal Insurance Expert. <highlight>In Your Digital Wallet.</highlight>'),
                {
                  highlight: ({ children }) => <span className={styles.highlight}>{children}</span>
                }
              )}
            </h1>

            {/* <p className={styles.subheadline}>
              {t('hero.subheadline_2', 'Stop overpaying. Stop guessing. Start knowing.')}
            </p> */}

            <div className={styles.ctaGroup}>
              <button
                className={styles.primaryCta}
                onClick={handleGetStarted}
              >
                <span>
                  {isAuthenticated
                    ? t('hero.cta_primary_auth', 'Go to Wallet')
                    : t('hero.cta_primary', 'Get Started Free')
                  }
                </span>
                <ArrowRight size={18} strokeWidth={2.5} />
              </button>

              <button
                className={styles.secondaryCta}
                onClick={() => {
                  document.getElementById('how-it-works')?.scrollIntoView({
                    behavior: 'smooth'
                  });
                }}
              >
                {t('hero.cta_secondary', 'See How It Works')}
              </button>
            </div>

            {/* Trust signals */}
            <div className={styles.trustSignals}>
              <div className={styles.trustItem}>
                <Lock size={14} strokeWidth={2.5} />
                <span>{t('hero.trust_encryption', 'End-to-end encryption')}</span>
              </div>
              <div className={styles.trustDot} />
              <div className={styles.trustItem}>
                <Zap size={14} strokeWidth={2.5} />
                <span>{t('hero.trust_free', 'Free forever to store')}</span>
              </div>
              <div className={styles.trustDot} />
              <div className={styles.trustItem}>
                <Shield size={14} strokeWidth={2.5} />
                <span>{t('hero.trust_no_sell', 'We never sell your data')}</span>
              </div>
            </div>
          </div>

          {/* Right: Floating image composition */}
          <div className={styles.imageBlock}>
            <div className={styles.imageComposition}>
              {/* Main image */}
              {/* <div className={`${styles.mainImage} ${isImageLoaded ? styles.loaded : ''}`}>
                <img
                  src={`${ASSETS_BASE_URL}/images/hero-family-car.png`}
                  alt={t('hero.image_alt', 'Family protected by insurance')}
                  loading="eager"
                  fetchPriority="high"
                />
              </div> */}

              {/* Floating product card */}
              {/* <div className={styles.floatingCard}> */}
                {/* <div className={styles.floatingCardIcon}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="var(--color-success)" />
                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div> */}
                {/* <div className={styles.floatingCardText}>
                  <span className={styles.floatingCardTitle}>{t('hero.floating_card_title', 'Policy Analyzed')}</span>
                  <span className={styles.floatingCardSub}>{t('hero.floating_card_sub', 'Auto · Full Coverage')}</span>
                </div> */}
              {/* </div> */}

              {/* Floating stat */}
              {/* <div className={styles.floatingStat}>
                <span className={styles.floatingStatValue}>92%</span>
                <span className={styles.floatingStatLabel}>{t('hero.floating_stat_label', 'Confidence')}</span>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Curved bottom divider */}
      <div className={styles.curvedDivider}>
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path d="M0,0 C360,120 1080,120 1440,0 L1440,120 L0,120 Z" fill="var(--bg-primary)" />
        </svg>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialType="sign_up"
      />
    </section>
  );
};

export default Hero;